import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { toZonedTime } from 'date-fns-tz'
import { Check } from '@phosphor-icons/react'
import { Formik } from 'formik'

import AdvancedSetupSection from '../components/AdvancedSetupSection'
import SaveAsTemplateModal from '../SaveAsTemplateModal/SaveAsTemplateModal'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import {
  getInspectionData,
  getTemplateData,
  getDaysOfWeekOptions,
  DEFAULT_TEMPLATE,
  DEFAULT_INSPECTION,
  DEFAULT_INSPECTION_FIELDS,
  DEFAULT_TEMPLATE_FIELDS,
  INSPECTION_ENTITY_TYPE,
  TRIGGER_TYPES,
  TRIGGER_OPTIONS,
  getInspectionsListPath,
} from '../helpers'
import { LOCATION_TYPES, getMultiUnitCautionText, toUnitArray } from '../unitSelection'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import API from '../../../api/api'

import makeValidationSchema from './validation'
import InspectionSettings from './components/InspectionSettings'
import Navbar from './components/Navbar'

const ScheduleInspectionView = ({ building }) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [isLoading, setIsLoading] = useState(false)
  const [saveAsTemplateVisible, setSaveAsTemplateVisible] = useState(false)
  const [submitType, setSubmitType] = useState(null)
  const [inspection, setInspection] = useState(null)
  const [template, setTemplate] = useState(null)
  const [isWarningPopupOpen, setIsWarningPopupOpen] = useState(false)
  const [createInspectionModalOpen, setCreateInspectionModalOpen] = useState(false)
  const [updateInspectionModalOpen, setUpdateInspectionModalOpen] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { inspectionId, trigger } = useParams()

  const isScheduledInspection =
    inspection && inspection.entityType === INSPECTION_ENTITY_TYPE.SCHEDULED

  const { displaySnackbar } = useSnackbar()

  const onBack = () => navigate(-1)

  const updateParams = () => {
    const path = `/dashboard/building-dashboard/internal-inspections/edit`
    navigate(`${path}/${inspection.id}`)
  }

  const checkLocation = () => {
    if (location.pathname.includes('/create')) {
      updateParams()
    }
  }

  const getInitialValues = () => {
    if (inspection) {
      const initial = [...DEFAULT_TEMPLATE_FIELDS, ...DEFAULT_INSPECTION_FIELDS].reduce(
        (acc, key) => {
          acc[key] = inspection[key]
          return acc
        },
        {},
      )
      initial.advanced = !!(initial.rooms && initial.rooms.length)

      if (initial.assignees && initial.assignees.length) {
        initial.assigneeIds = initial.assignees.map((assignee) => assignee.id)
      }

      if (initial.date && initial.recurrenceType === 'weekly') {
        const dayNum = toZonedTime(initial.date, building.timezone).getDay()
        initial.dayOfWeek = getDaysOfWeekOptions(t).find((day) => day.id === dayNum).value
      }

      // The multi-unit picker works with arrays; legacy single-unit setups store a string.
      // Scheduled instances keep the string — they stay on the single-select picker.
      if (initial.locationType === LOCATION_TYPES.UNIT && !isScheduledInspection) {
        initial.locationValue = toUnitArray(initial.locationValue)
      }

      initial.frequency =
        initial.recurrenceType === TRIGGER_TYPES.TRIGGER
          ? TRIGGER_TYPES.TRIGGER
          : TRIGGER_TYPES.TIME

      return initial
    }

    const recurrenceType =
      trigger === TRIGGER_OPTIONS.ONE_TIME
        ? TRIGGER_OPTIONS.ONE_TIME
        : DEFAULT_INSPECTION.recurrenceType

    return {
      ...DEFAULT_TEMPLATE,
      ...DEFAULT_INSPECTION,
      recurrenceType,
    }
  }

  const fetchInspectionById = async (id) => {
    try {
      setIsLoading(true)
      const { data } = await API.getInspectionById(id)
      setInspection(data)
    } catch {
      displaySnackbar(
        t('internal-inspections:scheduleInspectionView.errorFetchingInspection'),
        true,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const createTemplate = async (values) => {
    setIsLoading(true)

    const requestData = getTemplateData(values)

    try {
      const { data } = await API.createInspectionTemplate({
        buildingId: building.id,
        ...requestData,
      })
      setTemplate(data)
      displaySnackbar(t('internal-inspections:templateView.createdSuccessfully'), false)
    } catch {
      displaySnackbar(t('internal-inspections:templateView.errorCreating'), true)
    } finally {
      setSaveAsTemplateVisible(false)
      setSubmitType(null)
      setIsLoading(false)
    }
  }

  const updateInspection = async (values) => {
    setIsLoading(true)

    const inspectionData = getInspectionData(values, building)
    const templateData = getTemplateData(values)

    const requestData = { ...inspectionData, ...templateData }

    try {
      const { data } = isScheduledInspection
        ? await API.updateScheduledInspection(inspection.id, requestData)
        : await API.updateInspectionSetup(inspection.id, requestData)
      setInspection(data)
      displaySnackbar(
        t('internal-inspections:scheduleInspectionView.changesSavedSuccessfully'),
        false,
      )
    } catch {
      displaySnackbar(t('internal-inspections:scheduleInspectionView.errorSavingChanges'), true)
    } finally {
      setSubmitType(null)
      setIsLoading(false)
      setUpdateInspectionModalOpen(false)
    }
  }

  const createInspection = async (values) => {
    setIsLoading(true)
    const inspectionData = getInspectionData(values, building)

    let requestData = { ...inspectionData }

    if (template) {
      requestData.templateId = template.id
    } else {
      const templateData = getTemplateData(values)

      requestData = { ...requestData, ...templateData }
    }

    try {
      const createInspection = requestData.templateId
        ? API.createInspectionFromTemplate
        : API.createInspectionSetup
      await createInspection(requestData)
      displaySnackbar(t('internal-inspections:scheduleInspectionView.createdSuccessfully'), false)
      navigate(getInspectionsListPath(trigger))
    } catch {
      displaySnackbar(t('internal-inspections:scheduleInspectionView.errorCreating'), true)
    }
  }

  const onSubmit = (_, { setSubmitting }) => {
    if (submitType === 'inspection') {
      if (inspection) {
        setUpdateInspectionModalOpen(true)
      } else {
        setCreateInspectionModalOpen(true)
      }
    } else {
      setSaveAsTemplateVisible(true)
    }
    setSubmitting(false)
  }

  const getTemplateButton = (isValid) => {
    if (template) {
      return (
        <button disabled className="button button-lightgray" type="submit">
          <Check size={24} />
          {t('internal-inspections:scheduleInspectionView.savedAsTemplate')}
        </button>
      )
    } else if (inspection) {
      return inspection.templateId ? (
        <a
          href={`#/dashboard/building-dashboard/internal-inspections/template/edit/${inspection.templateId}`}
          target="_blank"
          className="button button-lightgray"
        >
          {t('internal-inspections:scheduleInspectionView.updateTemplate')}
        </a>
      ) : null
    } else {
      return (
        <button
          onClick={() => setSubmitType('template')}
          disabled={isLoading || !isValid || !!template}
          className="button button-lightgray"
          type="submit"
        >
          {t('internal-inspections:saveAsTemplateModal.title')}
        </button>
      )
    }
  }

  const getInspectionButton = (isValid, dirty) => (
    <button
      onClick={() => setSubmitType('inspection')}
      disabled={isLoading || !isValid || !dirty}
      className="button button-black"
      type="submit"
    >
      {inspection
        ? t('internal-inspections:scheduleInspectionView.save')
        : t('internal-inspections:createInspectionFromTemplate.createInspection')}
    </button>
  )

  const getCreateInspectionModalText = ({ name, locationType, locationValue }) => {
    const isArrayValue = Array.isArray(locationValue) && locationValue.length > 0

    if (locationType === LOCATION_TYPES.UNIT && isArrayValue && locationValue.length > 1) {
      return getMultiUnitCautionText(t, name, locationValue.length)
    }
    if (locationType === LOCATION_TYPES.UNIT_TYPES && isArrayValue) {
      return t(
        'internal-inspections:scheduleInspectionView.createInspectionModalTextWithUnitTypes',
        {
          name,
          unitTypes: locationValue.join(', '),
        },
      )
    }
    return t('internal-inspections:scheduleInspectionView.createInspectionModalText', { name })
  }

  const getUpdateInspectionModalText = () => {
    const inspectionType =
      inspection && inspection.recurrenceType === TRIGGER_TYPES.TRIGGER
        ? t('internal-inspections:scheduleInspectionView.triggerBasedInspectionType')
        : t('internal-inspections:scheduleInspectionView.timeBasedInspectionType')

    return t('internal-inspections:scheduleInspectionView.updateInspectionModalText', {
      inspectionType,
    })
  }

  useEffect(() => {
    if (inspection) {
      checkLocation()
    }
  }, [inspection])

  useEffect(() => {
    if (building && inspectionId && !inspection) {
      fetchInspectionById(inspectionId)
    }
  }, [building, inspection])

  return (
    <React.Fragment>
      <Formik
        validationSchema={makeValidationSchema(t)}
        initialValues={getInitialValues()}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          isValid,
          dirty,
          handleChange,
          setFieldValue,
          setValues,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit} className={`${isLoading ? 'is-loading' : ''}`}>
            <Navbar
              onBack={() => (dirty ? setIsWarningPopupOpen(true) : onBack())}
              templateButton={getTemplateButton(isValid)}
              inspectionButton={getInspectionButton(isValid, dirty)}
              isEdit={!!inspection}
            />
            <div className={`ctp-internal-inspections__view`}>
              <div className="ctp-internal-inspections__view__content left-side">
                <InspectionSettings
                  values={values}
                  errors={errors}
                  building={building}
                  isScheduledInspection={isScheduledInspection}
                  unitsPrefetchEnabled={!inspectionId || !!(inspection && !isScheduledInspection)}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                />
              </div>
              <div className="ctp-internal-inspections__view__content right-side">
                <AdvancedSetupSection
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
            {saveAsTemplateVisible && (
              <SaveAsTemplateModal
                onClose={() => setSaveAsTemplateVisible(false)}
                onSubmit={() => createTemplate(values)}
                handleChange={handleChange}
                value={values.templateName}
              />
            )}
            <ConfirmPopup
              isOpen={createInspectionModalOpen}
              cancelCallback={() => setCreateInspectionModalOpen(false)}
              confirmCallback={() => createInspection(values)}
              isLoading={isLoading}
              cancelText={t('common:actions.back')}
              confirmText={t('internal-inspections:scheduleInspectionView.confirmCreateInspection')}
              title={t('internal-inspections:dashboard.cautionTitle')}
              body={getCreateInspectionModalText(values)}
              className="is-caution"
            />
            <ConfirmPopup
              isOpen={updateInspectionModalOpen}
              cancelCallback={() => setUpdateInspectionModalOpen(false)}
              confirmCallback={() => updateInspection(values)}
              isLoading={isLoading}
              cancelText={t('common:actions.back')}
              confirmText={t('common:actions.confirm')}
              title={t('internal-inspections:dashboard.cautionTitle')}
              body={getUpdateInspectionModalText()}
              className="is-caution"
            />
          </form>
        )}
      </Formik>
      <ConfirmPopup
        isOpen={isWarningPopupOpen}
        cancelCallback={() => setIsWarningPopupOpen(false)}
        confirmCallback={onBack}
        cancelText={t('common:actions.back')}
        confirmText={t('internal-inspections:templateView.discardChanges')}
        title={t('internal-inspections:dashboard.cautionTitle')}
        body={t('internal-inspections:templateView.discardWarningBody')}
        className="is-caution"
      />
    </React.Fragment>
  )
}

ScheduleInspectionView.propTypes = {
  building: PropTypes.object,
}

export default connect(
  (state) => ({
    building: selectActiveBuilding(state),
  }),
  null,
)(ScheduleInspectionView)
